import React, { useState } from 'react'
import axios from 'axios'
import { Container, Form, FormGroup, FormField, Input, TextArea, Checkbox, Button, Icon, Message, Confirm, Divider } from 'semantic-ui-react'

const Settings = ({ settings, onStateChange, gameDeleted }) => {
    const [isHidden, setIsHidden] = useState(settings.isHidden)
    const [hasPackages, setHasPackages] = useState(settings.hasPackages)
    const [requiresServerID, setRequiresServerID] = useState(settings.requiresServerID)
    const [requiresCharacterName, setRequiresCharacterName] = useState(settings.requiresCharacterName)
    const [priority, setPriority] = useState(settings.priority)
    const [supplierName, setSupplierName] = useState(settings.supplierName)
    const [supplier, setSupplier] = useState(settings.supplier)
    const [supplierNotes, setSupplierNotes] = useState(settings.supplierNotes)
    const [consumerMarkup, setConsumerMarkup] = useState(settings.consumerMarkup)
    const [instructionalImage, setInstructionalImage] = useState(settings.instructionalImage)
    const [redemptionCodes, setRedemptionCodes] = useState(settings.redemptionCodes)
    const [isSyncingPrices, setIsSyncingPrices] = useState(false)
    const [showSyncSuccessMessage, setShowSyncSuccessMessage] = useState(false)
    const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] = useState(false)

    const handleChangeField = e => {
        const { name, value } = e.target
        if (name === 'priority') setPriority(value)
        if (name === 'supplierName') setSupplierName(value)
        if (name === 'supplier') setSupplier(value)
        if (name === 'supplierNotes') setSupplierNotes(value)
        if (name === 'consumerMarkup') setConsumerMarkup(value)
        if (name === 'instructionalImage') setInstructionalImage(value)
        if (name === 'redemptionCodes') setRedemptionCodes(value.split(',').map(code => code.trim()))
        onStateChange({
            priority: name === 'priority' ? value : priority,
            consumerMarkup: name === 'consumerMarkup' ? value : consumerMarkup,
            supplierName: name === 'supplierName' ? value : supplierName,
            supplier: name === 'supplier' ? value : supplier,
            supplierNotes: name === 'supplierNotes' ? value : supplierNotes,
            requiresServerID,
            requiresCharacterName,
            instructionalImage: name === 'instructionalImage' ? value : instructionalImage,
            hasPackages,
            redemptionCodes: name === 'redemptionCodes' ? value.split(',').map(code => code.trim()) : redemptionCodes,
            isHidden
        })
    }

    const handleToggle = (e, data) => {
        if (data.name === 'isHidden') setIsHidden(data.checked)
        if (data.name === 'hasPackages') setHasPackages(data.checked)
        if (data.name === 'requiresServerID') setRequiresServerID(data.checked)
        if (data.name === 'requiresCharacterName') setRequiresCharacterName(data.checked)
        onStateChange({
            priority,
            consumerMarkup,
            supplierName,
            supplier,
            supplierNotes,
            requiresServerID: data.name === 'requiresServerID' ? data.checked : requiresServerID,
            requiresCharacterName: data.name === 'requiresCharacterName' ? data.checked : requiresCharacterName,
            instructionalImage,
            hasPackages: data.name === 'hasPackages' ? data.checked : hasPackages,
            redemptionCodes,
            isHidden: data.name === 'isHidden' ? data.checked : isHidden,
        })
    }

    const handleSyncPrices = async () => {
        setIsSyncingPrices(true)
        const url = process.env.NODE_ENV === 'development'
            ? supplierName === 'mlbb' ? '/api/proxy/sync_prices/mlbb' : '/api/proxy/sync_prices/game/' + supplierName
            : supplierName === 'mlbb' ? '/api/sync_prices/mlbb' : '/api/sync_prices/game/' + supplierName
        await axios.post(url).then(res => {
            console.log(res.data)
            setIsSyncingPrices(false)
            setShowSyncSuccessMessage(true)
        }).catch(err => {
            console.log(`handleSyncPrices failed with error: ${err}`)
            alert(`Failure syncing prices for ${supplierName}, please try again. Error: ${err}`)
            setIsSyncingPrices(false)
        })
    }

    const handleDeleteClicked = () => {
        if (showDeleteConfirmationPopup) return
        setShowDeleteConfirmationPopup(true)
    }

    const handleDeleteCancel = () => {
        setShowDeleteConfirmationPopup(false)
    }

    const handleDeleteConfirm = async () => {
        await axios.delete('/api/game/' + settings.id).then(() => {
            setShowDeleteConfirmationPopup(false)
            gameDeleted()
        }).catch(err => {
            console.log(`delete reseller failed with error: ${err}`)
            alert(`Failure deleting reseller, please try again. Error: ${err}`)
        })
    }

    const getSyncButtonHeaderStr = () => {
        switch (supplierName) {
            case 'mlbb':
                return 'Sync Prices for mlbb, mlbb_global and mlbb_special'
            default:
                return `Sync Prices for ${supplierName}`
        }
    }

    return (
        <Container inverted style={{ background: '#1b1c1d', padding: '20px' }}>
            {showSyncSuccessMessage ? (
                <Message inverted style={{ marginTop: 20 }} success header='Successfully synced prices with supplier' content={supplierName === 'mlbb' ? 'For packages mlbb, mlbb_global and mlbb_special' : 'for ' + supplierName}/>
            ) : null}
            <h4 style={{ color: 'white' }}>{getSyncButtonHeaderStr()}</h4>
            <Button inverted loading={isSyncingPrices} style={{ marginBottom: 10 }} color='teal' onClick={handleSyncPrices}><Icon name='refresh'/>Sync Now</Button>
            <Divider inverted/>
            <Form inverted style={{ marginTop: 25 }}>
                <FormGroup>
                    <FormField control={Checkbox} label='Is Hidden' name='isHidden' checked={isHidden} onChange={handleToggle} />
                    <FormField control={Checkbox} label='Has Packages' name='hasPackages' checked={hasPackages} onChange={handleToggle}/>
                    <FormField control={Checkbox} label='Requires Server ID' name='requiresServerID' checked={requiresServerID} onChange={handleToggle}/>
                    <FormField control={Checkbox} label='Requires Character Name' name='requiresCharacterName' checked={requiresCharacterName} onChange={handleToggle}/>
                </FormGroup>
                <FormField control={Input} label='Priority' name='priority' value={priority} placeholder='Priority' onChange={handleChangeField} className="dark-theme-input"/>
                <FormField control={Input} disabled label="Supplier Game Name" name='supplierName' value={supplierName} placeholder='Supplier Game Name' onChange={handleChangeField} className="dark-theme-input"/>
                <FormField control={Input} disabled label='Supplier' name='supplier' value={supplier} placeholder='Supplier' onChange={handleChangeField} className="dark-theme-input"/>
                <FormField control={Input} label="Supplier's Notes" name='supplierNotes' value={supplierNotes} placeholder="Supplier's Notes" onChange={handleChangeField} className="dark-theme-input"/>
                <FormField control={Input} label='Consumer Mark Up %' name='consumerMarkup' value={consumerMarkup} placeholder='Consumer Mark Up %' onChange={handleChangeField} className="dark-theme-input"/>
                <FormField control={Input} label='Instructional Image' name='instructionalImage' value={instructionalImage} placeholder='Instructional Image' onChange={handleChangeField} className="dark-theme-input"/>
                <FormField control={TextArea} rows='2' label='Redemption Codes' name='redemptionCodes' value={redemptionCodes.join(', ')} placeholder='' onChange={handleChangeField} className="dark-theme-textarea-field"/>
            </Form>
            <Button inverted style={{ marginTop: 20 }} color='red' onClick={handleDeleteClicked}>
                <Icon name='trash'/>Delete this game
            </Button>
            <Confirm inverted open={showDeleteConfirmationPopup} onCancel={handleDeleteCancel} onConfirm={handleDeleteConfirm} header='Confirmation' content='Delete this game?' cancelButton='Cancel' confirmButton='Delete'/>
        </Container>
    )
}

export default Settings